import { Button, Muted, color, space } from '@biopeak/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDocumentViewUrl, type PatientDocument } from '../lib/documents.ts';
import { DocumentReview } from './DocumentReview.tsx';

/**
 * MODO CONFERÊNCIA: o exame e os analitos na mesma tela.
 *
 * O PROBLEMA QUE ISTO RESOLVE. "Abrir exame" mandava o PDF para uma aba nova,
 * e a revisão ficava na aba de trás. Validar um analito exige olhar o valor no
 * documento e o valor lido, lado a lado — com duas abas, isso vira alternância
 * de janela a cada linha. Num hemograma real de ~95 analitos são ~95
 * alternâncias, e a essa altura o médico já está clicando "confirmar todos"
 * sem olhar. O portão vira carimbo por CANSAÇO, não por descuido, e um portão
 * que carimba não é portão (ver as três regras em `DocumentReview`).
 *
 * SOBREPOSIÇÃO EM TELA CHEIA, e não um painel dentro da aba: o documento
 * precisa de altura para ser legível, e a aba já gasta a dela com cabeçalho,
 * abas e cartões. Aqui a única coisa na tela é a tarefa.
 *
 * A LISTA DA DIREITA É O MESMO `DocumentReview` DE SEMPRE, montado inteiro.
 * Nenhuma regra do portão foi reescrita aqui — o que exige decisão individual,
 * o que entra no lote e o que fica de fora continuam decididos num lugar só.
 * Esta tela é moldura, não lógica.
 */
export function DocumentSideBySide({
  documento,
  onClose,
}: {
  documento: PatientDocument;
  onClose: () => void;
}) {
  const url = useDocumentViewUrl(documento);
  // Proporção da coluna esquerda. Laudo em retrato pede mais largura para o
  // documento; tabela de hemograma pede mais para a lista — daí ser ajustável
  // em vez de fixa.
  const [larguraEsq, setLarguraEsq] = useState(55);
  const arrastando = useRef(false);
  const container = useRef<HTMLDivElement>(null);

  // Esc fecha. É o gesto que a pessoa tenta primeiro numa sobreposição, e não
  // atendê-lo faz a tela parecer presa.
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  // A página atrás não deve rolar enquanto a sobreposição está aberta.
  useEffect(() => {
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = anterior;
    };
  }, []);

  const aoMover = useCallback((e: MouseEvent) => {
    if (!arrastando.current || !container.current) return;
    const caixa = container.current.getBoundingClientRect();
    const pct = ((e.clientX - caixa.left) / caixa.width) * 100;
    // Nenhum dos lados pode sumir: abaixo de 25% a coluna deixa de servir para
    // o que existe.
    setLarguraEsq(Math.min(75, Math.max(25, pct)));
  }, []);

  useEffect(() => {
    function soltar() {
      arrastando.current = false;
      document.body.style.userSelect = '';
    }
    window.addEventListener('mousemove', aoMover);
    window.addEventListener('mouseup', soltar);
    return () => {
      window.removeEventListener('mousemove', aoMover);
      window.removeEventListener('mouseup', soltar);
    };
  }, [aoMover]);

  // PDF vai em `iframe` (o visualizador nativo do navegador dá zoom, busca e
  // paginação de graça); imagem vai em `img`, que o iframe exibiria sem
  // controle de ajuste à largura.
  const ehImagem = /\.(png|jpe?g|webp|heic)$/i.test(documento.storage_path);

  return (
    <div
      ref={container}
      style={{
        position: 'fixed',
        inset: 0,
        // Acima do cabeçalho fixo do painel, que fica em 20.
        zIndex: 50,
        background: color.white,
        display: 'flex',
        flexDirection: 'column',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Conferência do exame"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${space.sm} ${space.lg}`,
          borderBottom: `1px solid ${color.border}`,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: space.sm }}>
          <span style={{ fontSize: '15px', fontWeight: 600 }}>Conferência do exame</span>
          <Muted style={{ fontSize: '13px' }}>
            O documento à esquerda, o que foi lido à direita.
          </Muted>
        </div>
        <Button variant="quiet" onClick={onClose}>
          Fechar
        </Button>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div
          style={{
            width: `${larguraEsq}%`,
            minWidth: 0,
            background: color.surface,
            display: 'flex',
            overflow: ehImagem ? 'auto' : 'hidden',
          }}
        >
          {url.isLoading && (
            <div style={{ margin: 'auto' }}>
              <Muted>Abrindo o documento…</Muted>
            </div>
          )}

          {url.isError && (
            <div style={{ margin: 'auto', padding: space.lg, textAlign: 'center' }}>
              <Muted>
                Não foi possível abrir o arquivo agora. Feche e tente de novo.
              </Muted>
            </div>
          )}

          {url.data && (ehImagem ? (
            <img
              src={url.data}
              alt="Exame enviado pelo paciente"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          ) : (
            <iframe
              src={url.data}
              title="Exame enviado pelo paciente"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ))}
        </div>

        {/* Divisória arrastável. `onMouseDown` no elemento e o resto do
            arrasto na janela: seguir o mouse só enquanto ele está sobre a
            divisória faria o arrasto travar a cada movimento rápido. */}
        <div
          onMouseDown={() => {
            arrastando.current = true;
            // Sem isto o arrasto seleciona o texto da lista ao lado.
            document.body.style.userSelect = 'none';
          }}
          style={{
            width: '10px',
            cursor: 'col-resize',
            background: color.border,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          role="separator"
          aria-label="Ajustar largura das colunas"
        >
          <div
            style={{
              width: '2px',
              height: '28px',
              borderRadius: '999px',
              background: color.textSecondary,
              opacity: 0.5,
            }}
            aria-hidden
          />
        </div>

        <div
          style={{
            width: `${100 - larguraEsq}%`,
            minWidth: 0,
            overflowY: 'auto',
            padding: space.lg,
          }}
        >
          <DocumentReview documentId={documento.id} />
        </div>
      </div>
    </div>
  );
}
